import {createSelector} from '@reduxjs/toolkit'

import {uniqBy} from '@shared/helpers/array'
import {cloneDeep, isEmpty, isEqual, isNil} from '@shared/helpers/lang'
import {omit, values, mapValueKeys, reduceValueKeys, someValueKeys} from '@shared/helpers/object'

import {isNaverShopping} from '@web/helpers/userAgent'

import {SelectedCoupons} from './interfaces'
import {
    getDeliveryCouponInfo,
    getProductDiscountInfo,
    getDuplicatedCouponInfo,
    getProductCouponInfo,
    getStoreCouponInfo,
    getDeliveryCoupons,
    clearProductCoupon,
} from './utils/coupon'

import type {RootState, CouponView, ImmediateCouponView, SubscriptionDiscountPolicy} from './interfaces'

export const orderSheetCouponSelector = (state: RootState) => state.orderSheetCoupon

/** flat하게 정렬한 쿠폰 정보 selector  */
export const couponListInfoSelector = createSelector(
    orderSheetCouponSelector,
    ({product, deliveryFee: {couponsByDeliveryGroupKey}}) => {
        const couponList: CouponView[] = []
        const immediateCouponList: ImmediateCouponView[] = []

        values(product.immediateCouponsByOrderSheetItemId).forEach((coupons) => {
            if (isEmpty(coupons)) {
                return
            }
            coupons.forEach((coupon) => immediateCouponList.push(coupon))
        })

        values([
            product.couponsByOrderSheetItemId,
            product.duplicateCouponsByOrderSheetItemId,
            product.couponsByMerchantNo,
            couponsByDeliveryGroupKey,
        ]).forEach((couponRecord) => {
            if (isNil(couponRecord)) {
                return
            }
            mapValueKeys((coupons) => {
                coupons.forEach((coupon) => couponList.push(coupon))
            }, couponRecord)
        }, [])

        return {
            couponList: uniqBy((coupon) => coupon.couponId, couponList),
            immediateCouponList: uniqBy((coupon) => coupon.policyNo, immediateCouponList),
        }
    },
)

export const deliveryCouponListSelector = createSelector(
    orderSheetCouponSelector,
    ({deliveryFee: {couponsByDeliveryGroupKey}}) => {
        return getDeliveryCoupons({
            couponsByDeliveryGroupKey,
        })
    },
)

/** 쿠폰 정보 selector */
export const couponInfoSelector = createSelector(
    (state: RootState) => state.orderSheetCoupon.discountOptimization,
    (state: RootState) => state.orderSheetCoupon.deliveryFee.couponsByDeliveryGroupKey,
    (discountOptimization, couponsByDeliveryGroupKey) => {
        return {
            hasDeliveryCoupons: someValueKeys((coupons) => {
                return coupons.length > 0
            }, couponsByDeliveryGroupKey),
            isDiscountOptimizationNotApplied: !discountOptimization,
        }
    },
)

/** 상품 쿠폰 */
export const productCouponInfoSelector = createSelector(
    (state: RootState) => state.orderSheetCoupon.product.couponsByOrderSheetItemId,
    (state: RootState) => state.orderSheetCoupon.selected.productCoupon,
    (couponsByOrderSheetItemId, selected) => {
        const {coupons, selectedCoupons} = getProductCouponInfo({
            coupons: couponsByOrderSheetItemId,
            selectedCoupons: selected,
        })
        return {
            coupons,
            selectedCoupons,
        }
    },
)

/** 적용 진행중인 상품 쿠폰 */
export const simulateProductCouponInfoSelector = createSelector(
    (state: RootState) => state.orderSheetCoupon.product.couponsByOrderSheetItemId,
    (state: RootState) => state.orderSheetCoupon.beforeApplyCoupon.productCoupon,
    (couponsByOrderSheetItemId, selected) => {
        const {coupons, selectedCoupons} = getProductCouponInfo({
            coupons: couponsByOrderSheetItemId,
            selectedCoupons: selected,
        })
        return {
            coupons,
            selectedCoupons,
        }
    },
)

/** 즉시할인 쿠폰 */
export const immediateCouponInfoSelector = createSelector(
    (state: RootState) => state.orderSheetCoupon.product.immediateCouponsByOrderSheetItemId,
    (state: RootState) => state.orderSheetCoupon.selected.immediateCoupon,
    (immediateCouponsByOrderSheetItemId, selected) => {
        return {
            coupons: immediateCouponsByOrderSheetItemId,
            selectedCoupons: selected,
        }
    },
)

/** 적용 진행중인 즉시할인 쿠폰 */
export const simulateImmediateCouponInfoSelector = createSelector(
    (state: RootState) => state.orderSheetCoupon.product.immediateCouponsByOrderSheetItemId,
    (state: RootState) => state.orderSheetCoupon.beforeApplyCoupon.immediateCoupon,
    (immediateCouponsByOrderSheetItemId, selected) => {
        return {
            coupons: immediateCouponsByOrderSheetItemId,
            selectedCoupons: selected,
        }
    },
)

/** 중복할인 쿠폰 */
export const duplicateCouponInfoSelector = createSelector(
    (state: RootState) => state.orderSheetCoupon.product.duplicateCouponsByOrderSheetItemId,
    (state: RootState) => state.orderSheetCoupon.selected.duplicateCoupon,
    (duplicateCouponsByOrderSheetItemId, selected) => {
        return {
            coupons: getDuplicatedCouponInfo({
                coupons: duplicateCouponsByOrderSheetItemId,
                selectedCoupons: selected,
            }),
            selectedCoupons: selected,
        }
    },
)

/** 적용 진행중인 중복할인 쿠폰 */
export const simulateDuplicateCouponInfoSelector = createSelector(
    (state: RootState) => state.orderSheetCoupon.product.duplicateCouponsByOrderSheetItemId,
    (state: RootState) => state.orderSheetCoupon.beforeApplyCoupon.duplicateCoupon,
    (duplicateCouponsByOrderSheetItemId, selected) => {
        return {
            coupons: getDuplicatedCouponInfo({
                coupons: duplicateCouponsByOrderSheetItemId,
                selectedCoupons: selected,
            }),
            selectedCoupons: selected,
        }
    },
)

/** 선택된 복수구매할인 */
export const multiPurchaseDiscountSelector = createSelector(
    (state: RootState) => state.orderSheetCoupon.product.multiPurchaseDiscounts,
    (state: RootState) => state.orderSheetCoupon.selected.multiPurchaseCoupon,
    (multiPurchaseDiscounts, selected) => {
        return {
            discounts: multiPurchaseDiscounts,
            selected,
        }
    },
)

/** 최적화된 복수구매할인 */
export const optimizedMultiPurchaseDiscountSelector = createSelector(
    (state: RootState) => state.orderSheetCoupon.optimizedCoupon.multiPurchaseCoupon,
    (optimizedMultiPurchaseCoupon) => {
        return {
            optimizedMultiPurchaseCoupon,
        }
    },
)

/** 적용진행중인 복수구매할인 */
export const simulateMultiPurchaseDiscountSelector = createSelector(
    (state: RootState) => state.orderSheetCoupon.product.multiPurchaseDiscounts,
    (state: RootState) => state.orderSheetCoupon.beforeApplyCoupon.multiPurchaseCoupon,
    (multiPurchaseDiscounts, selected) => {
        return {
            discounts: multiPurchaseDiscounts,
            selected,
        }
    },
)

/** 선택된 정기구독할인 */
export const subscriptionDiscountSelector = createSelector(
    (state: RootState) => state.orderSheetCoupon.discount.productItem,
    (state: RootState) => state.orderSheetCoupon.selected.subscriptionCoupon,
    (discountsRecord, selected) => {
        return {
            discounts: mapValueKeys((discounts) => {
                return discounts.filter((discount) => discount.discountType === 'SUBSCRIPTION_DISCOUNT')
            }, discountsRecord),
            selected: selected ?? [],
        }
    },
)

/** orderSheetItem 적용된 할인 금액 selector */
export const productItemDiscountInfoSelector = createSelector(
    (state: RootState) => state.orderSheetCoupon.discount.productItem,
    (state: RootState) => state.orderSheetCoupon.product.multiPurchaseDiscounts,
    optimizedMultiPurchaseDiscountSelector,
    (productItem, multiPurchaseDiscounts, {optimizedMultiPurchaseCoupon}) => {
        const {
            discountAmountByItemId,
            productCouponDiscountAmountByItemId,
            multiPurchaseInfo,
            discountAmount,
            storeDiscountAmountByItemId,
        } = getProductDiscountInfo({
            productItem,
            multiPurchaseDiscounts,
            multiPurchaseDiscountItemIds: optimizedMultiPurchaseCoupon,
        })

        return {
            /** 상품별 적용된 할인 금액 (스토어 할인 포함) */
            discountAmountByItemId,
            /** 상품별 적용된 상품 할인 금액 (스토어 할인 제외) */
            productCouponDiscountAmountByItemId,
            /** 복수금액 할인 관련 정보 */
            multiPurchaseInfo,
            /** 복수구매할인을 제외한 상품별 총 할인 금액 */
            discountAmount,
            /** 상품별 적용된 스토어 할인 금액 */
            storeDiscountAmountByItemId,
        }
    },
)

export const deliveryDiscountInfoSelector = createSelector(
    (state: RootState) => state.orderSheetCoupon.discount.deliveryItem,
    (
        deliveryItem,
    ): Maybe<{
        discountAmountByDeliveryGroupKey: {[x: string]: number}
        discountAmount: number
    }> => {
        if (!deliveryItem) {
            return null
        }
        const discountAmountByDeliveryGroupKey = mapValueKeys((discount) => {
            return discount.discountAmount
        }, deliveryItem)
        const discountAmount = reduceValueKeys((acc, amount) => acc + amount, 0, discountAmountByDeliveryGroupKey)
        return {
            discountAmountByDeliveryGroupKey,
            discountAmount,
        }
    },
)

/** 할인/쿠폰 페이지 내 orderSheetItem 적용된 할인 금액 selector */
export const simulateProductItemDiscountInfoSelector = createSelector(
    (state: RootState) => state.orderSheetCoupon.simulateDiscount.productItem,
    (state: RootState) => state.orderSheetCoupon.product.multiPurchaseDiscounts,
    simulateMultiPurchaseDiscountSelector,
    (simulateProductItem, multiPurchaseDiscounts, {selected: multiPurchaseDiscountItemIds}) => {
        const {discountAmountByItemId, productCouponDiscountAmountByItemId, multiPurchaseInfo, discountAmount} =
            getProductDiscountInfo({
                productItem: simulateProductItem,
                multiPurchaseDiscounts,
                multiPurchaseDiscountItemIds,
            })

        return {
            /** 상품별 적용된 할인 금액 (스토어 할인 포함) */
            discountAmountByItemId,
            /** 상품별 적용된 상품 할인 금액 (스토어 할인 제외) */
            productCouponDiscountAmountByItemId,
            /** 복수금액 할인 관련 정보 */
            multiPurchaseInfo,
            /** 상품별 적용된 할인 금액 */
            discountAmount,
        }
    },
)

/** 배송 쿠폰 */
export const deliveryCouponInfoSelector = createSelector(
    (state: RootState) => state.orderSheetCoupon.deliveryFee.couponsByDeliveryGroupKey,
    (state: RootState) => state.orderSheetCoupon.selected.deliveryCoupon,
    (couponsByDeliveryGroupKey, selected) => {
        const {coupons, selectedCoupons} = getDeliveryCouponInfo({
            coupons: couponsByDeliveryGroupKey,
            selectedCoupons: selected,
        })
        return {
            coupons,
            selectedCoupons,
        }
    },
)

/** 적용 진행중인 배송 쿠폰 */
export const simulateDeliveryCouponInfoSelector = createSelector(
    (state: RootState) => state.orderSheetCoupon.deliveryFee.couponsByDeliveryGroupKey,
    (state: RootState) => state.orderSheetCoupon.beforeApplyCoupon.deliveryCoupon,
    (couponsByDeliveryGroupKey, selected) => {
        const {coupons, selectedCoupons} = getDeliveryCouponInfo({
            coupons: couponsByDeliveryGroupKey,
            selectedCoupons: selected,
        })
        return {
            coupons,
            selectedCoupons,
        }
    },
)

/** 스토어 쿠폰 */
export const storeCouponInfoSelector = createSelector(
    (state: RootState) => state.orderSheetCoupon.product.couponsByMerchantNo,
    (state: RootState) => state.orderSheetCoupon.selected.storeCoupon,
    (couponsByMerchantNo, selected) => {
        const {coupons, selectedCoupons} = getStoreCouponInfo({
            coupons: couponsByMerchantNo,
            selectedCoupons: selected,
        })
        return {
            // TODO: store coupon영역은 store 상품당 하나만 노출되므로 disabled 필드 재계산 불필요한 것으로 보임
            // 추후 검토후 삭제 가능하다면 불필요 로직 삭제 필요
            coupons,
            selectedCoupons,
            couponsByMerchantNo,
        }
    },
)

/** 적용 진행중인 스토어 쿠폰 */
export const simulateStoreCouponInfoSelector = createSelector(
    (state: RootState) => state.orderSheetCoupon.product.couponsByMerchantNo,
    (state: RootState) => state.orderSheetCoupon.beforeApplyCoupon.storeCoupon,
    (couponsByMerchantNo, selected) => {
        const {coupons, selectedCoupons} = getStoreCouponInfo({
            coupons: couponsByMerchantNo,
            selectedCoupons: selected,
        })
        return {
            // TODO: store coupon영역은 store 상품당 하나만 노출되므로 disabled 필드 재계산 불필요한 것으로 보임
            // 추후 검토후 삭제 가능하다면 불필요 로직 삭제 필요
            coupons,
            selectedCoupons,
            couponsByMerchantNo,
        }
    },
)

function makeCouponKeyInfo(selectedCoupons: SelectedCoupons) {
    const immediate = mapValueKeys((coupons) => coupons.map(({policyNo}) => policyNo), selectedCoupons.immediateCoupon)
    const product = mapValueKeys((coupons) => coupons.map(({couponId}) => couponId), selectedCoupons.productCoupon)
    const duplicate = reduceValueKeys<{[orderSheetItemId: string]: string}, {[OrderSheetItemId: string]: CouponView[]}>(
        (acc, coupons, orderSheetItemId) => {
            if (!isEmpty(coupons)) {
                return {
                    ...acc,
                    // 중복 쿠폰 선택은 한개만 전달
                    ...(coupons[0] && {[orderSheetItemId]: coupons[0].couponId}),
                }
            }
            return acc
        },
        {},
        selectedCoupons.duplicateCoupon,
    )
    const productAll = (function () {
        const result: {[orderSheetItemId: string]: string[]} = {}
        Object.entries(product).forEach(([key, value]) => {
            if (!isEmpty(value)) {
                result[key] = [...value]
            }
        })
        Object.entries(duplicate).forEach(([key, value]) => {
            if (value) {
                if (result[key]) {
                    result[key].push(value)
                } else {
                    result[key] = [value]
                }
            }
        })
        return result
    })()
    return {
        productAll,
        immediate,
        product,
        duplicate,
        // 선택된 쿠폰만 반영
        store: reduceValueKeys(
            (acc, coupons, merchantNo) => {
                if (coupons) {
                    return {
                        ...acc,
                        [merchantNo]: coupons.couponId,
                    }
                }
                return acc
            },
            {},
            selectedCoupons.storeCoupon,
        ),
        delivery: reduceValueKeys(
            (acc, coupon, deliveryGroupKey) => {
                if (coupon) {
                    return {
                        ...acc,
                        [deliveryGroupKey]: coupon.couponId,
                    }
                }
                return acc
            },
            {},
            selectedCoupons.deliveryCoupon,
        ),
        multiPurchase: selectedCoupons.multiPurchaseCoupon,
        subscription: selectedCoupons.subscriptionCoupon ?? [],
    }
}

/** 선택된 쿠폰 ID */
export const selectedCouponKeyInfoSelector = createSelector(
    productCouponInfoSelector,
    immediateCouponInfoSelector,
    duplicateCouponInfoSelector,
    storeCouponInfoSelector,
    deliveryCouponInfoSelector,
    multiPurchaseDiscountSelector,
    subscriptionDiscountSelector,
    (
        {selectedCoupons: productCoupon},
        {selectedCoupons: immediateCoupon},
        {selectedCoupons: duplicateCoupon},
        {selectedCoupons: storeCoupon},
        {selectedCoupons: deliveryCoupon},
        {selected: multiPurchaseCoupon},
        {selected: subscriptionCoupon},
    ) => {
        return makeCouponKeyInfo({
            deliveryCoupon,
            productCoupon,
            duplicateCoupon,
            immediateCoupon,
            storeCoupon,
            multiPurchaseCoupon,
            subscriptionCoupon,
        })
    },
)

/**
 * 개선 전 형상
 * 선택하려고 하는 쿠폰 ID (배송지 제외)
 **/
export const prevSelectedCouponKeyInfoSelector = createSelector(
    (state: RootState) => state.orderSheetCoupon.beforeApplyCoupon,
    (beforeApplyCoupon) => {
        return omit('delivery', makeCouponKeyInfo(beforeApplyCoupon))
    },
)

/**
 * 개선 형상
 * 선택하려고 하는 쿠폰 ID (배송지 제외)
 **/
export const prevTmpSelectedCouponKeyInfoSelector = createSelector(
    (state: RootState) => state.orderSheetCoupon.beforeApplyTemporaryCoupon,
    (beforeApplyTemporaryCoupon) => {
        return makeCouponKeyInfo(beforeApplyTemporaryCoupon)
    },
)

/** 최적쿠폰 적용상태 여부를 반환 */
export const optimizedCouponsSelector = createSelector(
    orderSheetCouponSelector,
    deliveryCouponListSelector,
    (
        {
            deliveryFee: {isOptimized},
            discountOptimization,
            optimizedCoupon,
            selected,
            beforeApplyCoupon,
            myOptimizedInfo,
        },
        deliveryCoupons,
    ) => {
        function getValidRecord<T>(record: {[key: string]: T}) {
            return reduceValueKeys<{[key: string]: T}, {[key: string]: T}>(
                (acc, coupons, key) => {
                    if (!isEmpty(coupons)) {
                        acc[key] = coupons
                    }
                    return acc
                },
                {},
                record,
            )
        }

        const validOptimizedCoupon: SelectedCoupons = {
            deliveryCoupon: getValidRecord<Maybe<CouponView>>(optimizedCoupon.deliveryCoupon),
            productCoupon: getValidRecord<CouponView[]>(optimizedCoupon.productCoupon),
            duplicateCoupon: getValidRecord<CouponView[]>(optimizedCoupon.duplicateCoupon),
            immediateCoupon: getValidRecord<ImmediateCouponView[]>(optimizedCoupon.immediateCoupon),
            storeCoupon: getValidRecord<Maybe<CouponView>>(optimizedCoupon.storeCoupon),
            multiPurchaseCoupon: !isEmpty(optimizedCoupon.multiPurchaseCoupon)
                ? optimizedCoupon.multiPurchaseCoupon
                : [],
        }

        const validSelectedCoupon: SelectedCoupons = {
            deliveryCoupon: getValidRecord<Maybe<CouponView>>(selected.deliveryCoupon),
            productCoupon: getValidRecord<CouponView[]>(selected.productCoupon),
            duplicateCoupon: getValidRecord<CouponView[]>(selected.duplicateCoupon),
            immediateCoupon: getValidRecord<ImmediateCouponView[]>(selected.immediateCoupon),
            storeCoupon: getValidRecord<Maybe<CouponView>>(selected.storeCoupon),
            multiPurchaseCoupon: !isEmpty(selected.multiPurchaseCoupon) ? selected.multiPurchaseCoupon : [],
        }

        const validBeforeApplyCoupon: SelectedCoupons = {
            deliveryCoupon: getValidRecord<Maybe<CouponView>>(beforeApplyCoupon.deliveryCoupon),
            productCoupon: getValidRecord<CouponView[]>(beforeApplyCoupon.productCoupon),
            duplicateCoupon: getValidRecord<CouponView[]>(beforeApplyCoupon.duplicateCoupon),
            immediateCoupon: getValidRecord<ImmediateCouponView[]>(beforeApplyCoupon.immediateCoupon),
            storeCoupon: getValidRecord<Maybe<CouponView>>(beforeApplyCoupon.storeCoupon),
            multiPurchaseCoupon: !isEmpty(beforeApplyCoupon.multiPurchaseCoupon)
                ? beforeApplyCoupon.multiPurchaseCoupon
                : [],
        }
        const validMyOptimizedCoupon: SelectedCoupons | undefined = myOptimizedInfo
            ? {
                  deliveryCoupon: getValidRecord<Maybe<CouponView>>(myOptimizedInfo.optimizedCoupon.deliveryCoupon),
                  productCoupon: getValidRecord<CouponView[]>(myOptimizedInfo.optimizedCoupon.productCoupon),
                  duplicateCoupon: getValidRecord<CouponView[]>(myOptimizedInfo.optimizedCoupon.duplicateCoupon),
                  immediateCoupon: getValidRecord<ImmediateCouponView[]>(
                      myOptimizedInfo.optimizedCoupon.immediateCoupon,
                  ),
                  storeCoupon: getValidRecord<Maybe<CouponView>>(myOptimizedInfo.optimizedCoupon.storeCoupon),
                  multiPurchaseCoupon: !isEmpty(myOptimizedInfo.optimizedCoupon.multiPurchaseCoupon)
                      ? myOptimizedInfo.optimizedCoupon.multiPurchaseCoupon
                      : [],
              }
            : undefined

        const useableDeliveryCoupons = deliveryCoupons.filter(
            (coupon) => !coupon.disabled && !(coupon.locationType === 'SHOPPING_APP' && !isNaverShopping),
        )
        /**
         * 아래 케이스에서는 할인페이지에서는 배송비 최대할인 적용된거로 간주 (상품쪽에서는 최대적용중 미노출)
         * > 배송비 쿠폰 존재 && 배송비 최적조합 적용불가능한 경우 (기획서상 2-2-2 case)
         *
         **/
        const canUseUpdateDiscount = !isEmpty(useableDeliveryCoupons) && isEmpty(optimizedCoupon.deliveryCoupon)
        /** 이미 선택된 쿠폰과 최적조합 비교 */
        const isSelectedOptimized = isEqual(validOptimizedCoupon, validSelectedCoupon)
        /** 선택중인 쿠폰과 최적조합 비교 */
        const isSelectingOptimized = canUseUpdateDiscount
            ? isEqual(omit('deliveryCoupon', validOptimizedCoupon), omit('deliveryCoupon', validBeforeApplyCoupon))
            : isEqual(validOptimizedCoupon, validBeforeApplyCoupon)
        /** 선택된 쿠폰과 나의할인가 최적조합 비교 */
        const isSelectingOptimizedFromMyDiscount = myOptimizedInfo
            ? isEqual(validSelectedCoupon, validMyOptimizedCoupon)
            : false
        /** 이미 선택된 쿠폰과 선택중인 쿠폰 비교 */
        const isSelectingChanged = !isEqual(validSelectedCoupon, validBeforeApplyCoupon)

        return {
            /**
             * 아래 케이스에서는 할인페이지에서는 배송비 최대할인 적용된거로 간주 (상품쪽에서는 최대적용중 미노출)
             * > 배송비 쿠폰 존재 && 배송비 최적조합 적용불가능한 경우
             **/
            canUseUpdateDiscount,
            deliveryOptimized: isOptimized,
            productOptimized: discountOptimization,
            isSelectedOptimized,
            isSelectingOptimized,
            isSelectingOptimizedFromMyDiscount,
            isSelectingChanged,
        }
    },
)

export const subscriptionDiscountPolicySelector = createSelector(
    orderSheetCouponSelector,
    ({subscriptionDiscountPolicesByOrderSheetItemId}) => {
        const nearestPolicyByItemId: Record<string, SubscriptionDiscountPolicy> = {}
        const appliedPolicyByItemId: Record<string, SubscriptionDiscountPolicy> = {}
        const policesByItemId = cloneDeep(subscriptionDiscountPolicesByOrderSheetItemId)

        Object.entries(policesByItemId).forEach(([itemId, polices]) => {
            const nearestPolicy = polices.sort((a, b) => a.discountStartPeriod - b.discountStartPeriod)[0]

            if (!nearestPolicy) {
                return
            }

            nearestPolicyByItemId[itemId] = nearestPolicy

            /** 할인 적용 시작 회차가 1이면 당장 적용되는 쿠폰 */
            if (nearestPolicy.discountStartPeriod === 1) {
                appliedPolicyByItemId[itemId] = nearestPolicy
            }
        })

        return {
            /** itemId별 할인 적용 시작 회차가 가장 가까운 할인 정책 */
            nearestPolicyByItemId,
            /** itemId별 1회차에 적용되는 할인 정책 */
            appliedPolicyByItemId,
            /** itemId별 할인 정책 전체 */
            policesByItemId,
        }
    },
)

// 선택된 쿠폰과 나의할인가 쿠폰조합이 서로 다른지 여부
export const selectedCouponDifferentFromMyOptimalCouponSelector = createSelector(
    orderSheetCouponSelector,
    ({selected, myOptimizedInfo}) => {
        if (!myOptimizedInfo) {
            return false
        }
        const filteredSelected = {
            deliveryCoupon: clearProductCoupon({selected: selected.deliveryCoupon}),
            productCoupon: clearProductCoupon({selected: selected.productCoupon}),
            duplicateCoupon: clearProductCoupon({selected: selected.duplicateCoupon}),
            immediateCoupon: clearProductCoupon({selected: selected.immediateCoupon}),
            storeCoupon: clearProductCoupon({selected: selected.storeCoupon}),
        }
        const filteredOptimized = {
            deliveryCoupon: clearProductCoupon({selected: myOptimizedInfo.optimizedCoupon.deliveryCoupon}),
            productCoupon: clearProductCoupon({selected: myOptimizedInfo.optimizedCoupon.productCoupon}),
            duplicateCoupon: clearProductCoupon({selected: myOptimizedInfo.optimizedCoupon.duplicateCoupon}),
            immediateCoupon: clearProductCoupon({selected: myOptimizedInfo.optimizedCoupon.immediateCoupon}),
            storeCoupon: clearProductCoupon({selected: myOptimizedInfo.optimizedCoupon.storeCoupon}),
        }

        return !isEqual(filteredSelected, filteredOptimized)
    },
)
